import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { authAPI } from '../../services/api';
import { toast } from 'sonner';
import { Mail, Loader2, Check } from 'lucide-react';

const VerificationRequiredModal = ({ open, onClose, featureName = 'this feature' }) => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleResend = async () => {
    setSending(true);
    try {
      const res = await authAPI.resendVerification();
      setSent(true);
      toast.success(res.data.message || 'Verification email sent. Check your inbox');
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      
      if (status === 429) {
        toast.error(detail || 'Please wait before requesting another email');
      } else if (status === 503) {
        // Email quota exceeded
        toast.error(detail || 'Email service temporarily unavailable. Please try again later.');
      } else {
        toast.error(detail || 'Failed to send verification email. Please try again later.');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#1A1A2E] border-[#2A2A4A] text-white max-w-md">
        <DialogHeader>
          <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center mb-4">
            <Mail className="w-6 h-6 text-amber-400" />
          </div>
          <DialogTitle className="text-xl">Verify your email to unlock {featureName}</DialogTitle>
          <DialogDescription className="text-[#8888AA]">
            Email verification is required to access this feature. Check your inbox for the verification link we sent when you signed up.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-[#8888AA] text-sm mb-4">
            Didn't receive the email? Click below to send a new verification link.
          </p>
          
          <Button
            onClick={handleResend}
            disabled={sending || sent}
            className={`w-full rounded-xl ${
              sent 
                ? 'bg-green-600 hover:bg-green-600' 
                : 'bg-[#E94560] hover:bg-[#E94560]/90'
            } text-white`}
            data-testid="modal-resend-btn"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : sent ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Verification email sent. Check your inbox
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Resend verification email
              </>
            )}
          </Button>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={onClose}
            className="border-[#2A2A4A] text-[#0A0F1E] hover:bg-[#16213E] rounded-xl"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VerificationRequiredModal;
